import PropTypes from 'prop-types';
import { clsx } from 'clsx';

/**
 * Reusable FormInput component with validation support
 */
const FormInput = ({
  label,
  name,
  type = 'text',
  placeholder = '',
  value,
  onChange,
  onBlur,
  error,
  touched,
  required = false,
  disabled = false,
  className = '',
  inputClassName = '',
  helperText,
  icon: Icon,
  ...props
}) => {
  const hasError = touched && error;

  return (
    <div className={clsx('mb-4', className)}>
      {label && (
        <label 
          htmlFor={name} 
          className="block text-sm font-medium text-neutral-700 mb-2"
        >
          {label}
          {required && <span className="text-accent-500 ml-1" aria-hidden="true">*</span>}
        </label>
      )}
      
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-neutral-400" aria-hidden="true" />
          </div>
        )}
        
        <input
          id={name}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-describedby={hasError ? `${name}-error` : helperText ? `${name}-helper` : undefined}
          className={clsx(
            'w-full px-4 py-3 rounded-xl border bg-white text-neutral-800',
            'placeholder-neutral-400 transition-all duration-200',
            'focus:outline-none focus:ring-4',
            Icon && 'pl-10',
            hasError
              ? 'border-accent-500 focus:border-accent-500 focus:ring-accent-100'
              : 'border-neutral-200 focus:border-primary-500 focus:ring-primary-100',
            disabled && 'bg-neutral-50 cursor-not-allowed',
            inputClassName
          )}
          {...props}
        />
      </div>

      {hasError && (
        <p 
          id={`${name}-error`} 
          className="text-sm text-accent-600 mt-1.5 flex items-center gap-1"
          role="alert"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}

      {helperText && !hasError && (
        <p id={`${name}-helper`} className="text-sm text-neutral-500 mt-1.5">
          {helperText}
        </p>
      )}
    </div>
  );
};

FormInput.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  touched: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  helperText: PropTypes.string,
  icon: PropTypes.elementType,
};

export default FormInput;
